/**
 * Platform detection utility
 * Detects if running on web, React Native (iOS/Android), or tablet
 */

// Check if running in React Native environment
export const isReactNative = () => {
  return (
    typeof navigator !== 'undefined' &&
    navigator.product === 'ReactNative'
  ) || (
    typeof window !== 'undefined' &&
    window.navigator?.userAgent?.includes('ReactNative')
  );
};

// Check if running on web (browser)
export const isWeb = () => {
  return typeof window !== 'undefined' && 
         !isReactNative() &&
         typeof document !== 'undefined';
};

// Check if running on iOS
export const isIOS = () => {
  if (isReactNative()) {
    try {
      // eslint-disable-next-line no-new-func
      const requireModule = new Function('moduleName', 'return require(moduleName)');
      const { Platform } = requireModule('react-native');
      return Platform.OS === 'ios';
    } catch (e) {
      // React Native not available (web environment)
      return false;
    }
  }
  if (typeof navigator !== 'undefined') {
    return /iPad|iPhone|iPod/.test(navigator.userAgent);
  }
  return false;
};

// Check if running on Android
export const isAndroid = () => {
  if (isReactNative()) {
    try {
      // eslint-disable-next-line no-new-func
      const requireModule = new Function('moduleName', 'return require(moduleName)');
      const { Platform } = requireModule('react-native');
      return Platform.OS === 'android';
    } catch (e) {
      // React Native not available (web environment)
      return false;
    }
  }
  if (typeof navigator !== 'undefined') {
    return /Android/.test(navigator.userAgent);
  }
  return false;
};

// Check if device is a tablet (approximate detection)
export const isTablet = () => {
  if (isReactNative()) {
    try {
      // eslint-disable-next-line no-new-func
      const requireModule = new Function('moduleName', 'return require(moduleName)');
      const { Platform, Dimensions } = requireModule('react-native');
      const { width, height } = Dimensions.get('window');
      const aspectRatio = height / width;
      return (
        (Platform.OS === 'ios' && Platform.isPad) ||
        (Platform.OS === 'android' && (width >= 600 || (width >= 480 && aspectRatio < 1.6)))
      );
    } catch (e) {
      // React Native not available (web environment)
      // Fall through to web detection
    }
  }
  // Web tablet detection (approximate)
  if (typeof navigator !== 'undefined' && typeof window !== 'undefined') {
    return /iPad/.test(navigator.userAgent) || 
           (window.innerWidth >= 768 && window.innerWidth < 1024);
  }
  return false;
};

// Get current platform string
export const getPlatform = () => {
  if (isIOS()) return 'ios';
  if (isAndroid()) return 'android';
  if (isWeb()) return 'web';
  return 'unknown';
};

// Get device type
export const getDeviceType = () => {
  if (isTablet()) return 'tablet';
  if (isReactNative()) return 'mobile';
  if (isWeb()) {
    const width = window.innerWidth;
    if (width >= 1024) return 'desktop';
    if (width >= 768) return 'tablet';
    return 'mobile';
  }
  return 'unknown';
};

const platformUtils = {
  isReactNative,
  isWeb,
  isIOS,
  isAndroid,
  isTablet,
  getPlatform,
  getDeviceType
};

export default platformUtils;

